// Converts the compact cohort_logic response into a ReactFlow graph.  Layout and
// presentation deliberately stay in AgentFlowLogic so the response contract is
// only concerned with cohort meaning.

const OPERATORS = new Set(['AND', 'OR']);

const isCount = (value) => Number.isSafeInteger(value) && value >= 0;

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const normaliseOperator = (value) => (OPERATORS.has(value) ? value : null);

const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');

const normaliseChild = (child, ancestors = new WeakSet()) => {
    if (!isPlainObject(child) || ancestors.has(child)) return null;

    const type = child.type;
    const id = cleanText(child.id);
    if (!id || !isCount(child.count)) return null;

    if (type === 'filter') {
        const label = cleanText(child.label);
        return label ? { type, id, label, count: child.count } : null;
    }

    if (type !== 'group') return null;
    const operator = normaliseOperator(child.operator);
    if (!operator || !Array.isArray(child.children)) return null;


    // WeakSet cannot be cloned.  The active-path marker below catches circular
    // data without rejecting a harmless shared child object in a malformed payload.
    ancestors.add(child);
    const children = child.children
        .map((item) => normaliseChild(item, ancestors))
        .filter(Boolean);
    ancestors.delete(child);

    return children.length
        ? { type, id, label: cleanText(child.label), operator, count: child.count, children }
        : null;
};

/**
 * Validates the cohort_logic envelope and removes invalid descendants.  A bad
 * root returns null; bad children are skipped so a partial diagram can still be
 * displayed safely.
 */
export const normalizeCohortLogic = (cohortLogic) => {
    if (!isPlainObject(cohortLogic) || !isCount(cohortLogic.root_count) || !isCount(cohortLogic.final_count)) return null;

    const operator = normaliseOperator(cohortLogic.operator);
    if (!operator || !Array.isArray(cohortLogic.children)) return null;

    const children = cohortLogic.children.map((child) => normaliseChild(child)).filter(Boolean);
    if (!children.length) return null;

    return {
        root_count: cohortLogic.root_count,
        operator,
        children,
        final_count: cohortLogic.final_count,
    };
};

export const validateCohortLogic = (cohortLogic) => {
    const value = normalizeCohortLogic(cohortLogic);
    return { valid: Boolean(value), value };
};

const displayCount = (count) => Number(count).toLocaleString('en-US');

/**
 * Creates raw ReactFlow-compatible nodes and edges.  It intentionally provides
 * neither positions nor styles; the caller owns layout and visual treatment.
 */
export const cohortLogicToReactFlow = (cohortLogic) => {
    const logic = normalizeCohortLogic(cohortLogic);
    if (!logic) return { nodes: [], edges: [] };

    const nodes = [];
    const edges = [];
    let nodeIndex = 0;
    let edgeIndex = 0;

    const addNode = (kind, label, semanticId, type) => {
        const id = `${kind}-${nodeIndex++}`;
        nodes.push({ id, ...(type ? { type } : {}), data: { label, semanticId } });
        return id;
    };
    const connect = (source, target) => {
        edges.push({ id: `edge-${edgeIndex++}`, source, target });
    };

    const rootId = addNode('root', `All Patients\nCount: ${displayCount(logic.root_count)}`, 'root', 'input');

    const renderSequence = (children, inputId) => children.reduce((currentId, child) => {
        if (child.type === 'filter') {
            const filterId = addNode('filter', `${child.label}\nCount: ${displayCount(child.count)}`, child.id);
            connect(currentId, filterId);
            return filterId;
        }

        return renderGroup(child, currentId);
    }, inputId);

    const renderGroup = (group, inputId) => {
        if (group.operator === 'AND') {
            const lastChildId = renderSequence(group.children, inputId);
            const groupId = addNode('group', `${group.label || 'AND'}\nCount: ${displayCount(group.count)}`, group.id);
            connect(lastChildId, groupId);
            return groupId;
        }

        const branchEnds = group.children.map((child) => renderSequence([child], inputId));
        const unionId = addNode('union', `${group.label || 'OR'}\nCount: ${displayCount(group.count)}`, group.id);
        branchEnds.forEach((branchId) => connect(branchId, unionId));
        return unionId;
    };

    const lastId = logic.operator === 'AND'
        ? renderSequence(logic.children, rootId)
        : (() => {
            const branchEnds = logic.children.map((child) => renderSequence([child], rootId));
            const unionId = addNode('union', `OR\nCount: ${displayCount(logic.final_count)}`, 'root-or');
            branchEnds.forEach((branchId) => connect(branchId, unionId));
            return unionId;
        })();

    const finalId = addNode('final', `Final Cohort\nCount: ${displayCount(logic.final_count)}`, 'final', 'output');
    connect(lastId, finalId);

    return { nodes, edges };
};

export default cohortLogicToReactFlow;
